/**
 * Bounded accumulation of MediaRecorder Blobs into S3 multipart parts.
 *
 * THE INVARIANT: the browser must never hold the whole recording. Bytes are
 * released as soon as a part is acknowledged, so peak memory is bounded by the
 * hard ceiling no matter how long the interview runs. The old path had no such
 * bound at all — the realtime context's retry queue grew without limit.
 *
 * WHY A DROPPED PART IS NOT AN OPTION. A MediaRecorder timeslice stream is one
 * continuous byte stream: the first Blob carries the EBML header, later Blobs
 * are bare continuation bytes. Removing one from the middle corrupts every byte
 * after it. So when we cannot keep up, the only safe lever is to stop producing
 * data — `MediaRecorder.pause()` — which yields a valid file with a time gap.
 * Truncating the recording or discarding a queued part would both be worse.
 *
 * PART SIZING. S3 requires every part except the last to be >= 5 MiB. Parts are
 * cut on whole-Blob boundaries once the accumulator REACHES the target, so a
 * part lands between the target and the target + one Blob. No Blob slicing, and
 * the 5 MiB floor cannot be violated by a mistimed flush. The final part is
 * exempt and may be any size.
 *
 * THE TARGET IS A DATA-LOSS WINDOW (VA-28098). Bytes below the threshold live
 * only in this page. A reload destroys them, and if the threshold was never
 * reached the upload has no parts at all, so the server aborts it and the whole
 * session's video is gone — that is how interview-b07254a4 lost its first half.
 * The target is therefore S3's floor exactly, not the floor plus headroom: the
 * headroom bought nothing (a part is always >= the target by construction) and
 * cost ~14s of recording per MiB at the bitrate a real interview measures.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export const MIB = 1024 * 1024;
/** S3's hard floor for every part except the last. */
export const S3_MIN_PART_BYTES = 5 * MIB;
/** S3's ceiling on part numbers. Past it, a part can be sent but never completed. */
export const S3_MAX_PART_NUMBER = 10000;
export const DEFAULT_PART_BUFFER_OPTIONS = {
    // S3's floor exactly — see PART SIZING above. The server's `partSize` still
    // overrides this, so the window can be retuned without a frontend deploy.
    targetPartBytes: S3_MIN_PART_BYTES,
    softWatermarkBytes: 32 * MIB,
    hardCeilingBytes: 96 * MIB,
    resumeWatermarkBytes: 16 * MIB,
    startPartNumber: 1,
};
/**
 * Accumulates Blobs, cuts parts, tracks outstanding bytes, signals backpressure.
 *
 * Deliberately does NOT own retry or presigning: it accounts for memory and
 * ordering, and nothing else. The uploader owns the network.
 */
export class PartBuffer {
    constructor(hooks, options = {}) {
        this.hooks = hooks;
        this.pending = [];
        this.pendingBytes = 0;
        this.inFlightBytes = 0;
        this.paused = false;
        this.softNotified = false;
        this.closed = false;
        this.opts = Object.assign(Object.assign({}, DEFAULT_PART_BUFFER_OPTIONS), options);
        if (this.opts.targetPartBytes < S3_MIN_PART_BYTES) {
            throw new Error(`targetPartBytes ${this.opts.targetPartBytes} is below the S3 minimum ${S3_MIN_PART_BYTES}`);
        }
        // Fail here rather than at completion time. A part numbered outside S3's
        // range uploads happily and is only rejected when the whole recording is
        // assembled — by which point the interview is over and the bytes are the
        // only copy.
        const start = this.opts.startPartNumber;
        if (!Number.isInteger(start) || start < 1 || start > S3_MAX_PART_NUMBER) {
            throw new Error(`startPartNumber ${start} is outside S3's range 1..${S3_MAX_PART_NUMBER}`);
        }
        this.nextPartNumber = start;
    }
    /** Bytes held in memory: accumulated-but-not-cut plus cut-but-not-acked. */
    get outstandingBytes() {
        return this.pendingBytes + this.inFlightBytes;
    }
    get isPaused() {
        return this.paused;
    }
    /** Parts THIS buffer has cut — not the upload's total, which may include an
     *  earlier session's parts when resuming (VA-28007). */
    get partsStarted() {
        return this.nextPartNumber - this.opts.startPartNumber;
    }
    /**
     * Take one Blob from the recorder.
     *
     * Never rejects for backpressure — it signals instead, because rejecting here
     * would mean discarding recorded video.
     */
    add(blob) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (this.closed || !blob || blob.size === 0)
                return;
            // Before the cut, so the tag is the part this Blob will land in:
            // `nextPartNumber` is only incremented when a part is actually cut.
            (_b = (_a = this.hooks).persistChunk) === null || _b === void 0 ? void 0 : _b.call(_a, blob, this.nextPartNumber);
            this.pending.push(blob);
            this.pendingBytes += blob.size;
            this.checkWatermarks();
            while (this.pendingBytes >= this.opts.targetPartBytes) {
                this.cutAndUpload(false);
            }
        });
    }
    /**
     * Flush whatever remains as the final part.
     *
     * The final part is exempt from the 5 MiB floor. If nothing is pending and no
     * part was ever cut, there is no recording to complete.
     */
    flushFinal() {
        return __awaiter(this, void 0, void 0, function* () {
            this.closed = true;
            if (this.pendingBytes > 0)
                this.cutAndUpload(true);
        });
    }
    cutAndUpload(isFinal) {
        if (this.pendingBytes === 0)
            return;
        const blobs = this.pending;
        const size = this.pendingBytes;
        this.pending = [];
        this.pendingBytes = 0;
        const part = {
            partNumber: this.nextPartNumber++,
            blob: new Blob(blobs),
            size,
        };
        // Guard the S3 floor for non-final parts. Reaching this means the options
        // were inconsistent; failing loudly beats producing an upload S3 rejects
        // only at completion time, long after the evidence is gone.
        if (!isFinal && size < S3_MIN_PART_BYTES) {
            throw new Error(`part ${part.partNumber} is ${size}B, below the S3 minimum`);
        }
        this.inFlightBytes += size;
        void this.hooks
            .uploadPart(part)
            .then(() => {
            var _a, _b;
            // Acknowledged by S3, and only now. Dropping the held copy any earlier
            // would reintroduce exactly the window this exists to close.
            (_b = (_a = this.hooks).dropPersistedPart) === null || _b === void 0 ? void 0 : _b.call(_a, part.partNumber);
        })
            .catch(() => {
            /* The uploader owns retry/reporting. We only stop accounting for it,
               so a permanently failed part cannot pin memory forever. The held
               copy stays: a part that never landed is precisely what a later page
               must be able to recover. */
        })
            .finally(() => {
            this.inFlightBytes = Math.max(0, this.inFlightBytes - size);
            this.checkWatermarks();
        });
    }
    checkWatermarks() {
        var _a, _b, _c, _d, _e, _f;
        const out = this.outstandingBytes;
        if (!this.paused && out >= this.opts.hardCeilingBytes) {
            this.paused = true;
            (_b = (_a = this.hooks).onPause) === null || _b === void 0 ? void 0 : _b.call(_a, out);
            return;
        }
        if (this.paused && out <= this.opts.resumeWatermarkBytes) {
            this.paused = false;
            this.softNotified = false;
            (_d = (_c = this.hooks).onResume) === null || _d === void 0 ? void 0 : _d.call(_c, out);
            return;
        }
        if (!this.softNotified && out >= this.opts.softWatermarkBytes) {
            this.softNotified = true;
            (_f = (_e = this.hooks).onSoftLimit) === null || _f === void 0 ? void 0 : _f.call(_e, out);
        }
    }
}
